import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Copy, Loader2, Check, ArrowRight, LayoutTemplate, Trash2, ShieldCheck } from 'lucide-react';
import { aaiApi } from '../../../services/aaiApi';
import type { ProgramTemplate, Workspace } from '../../../types/aai';
import { Loading, ErrorNote } from '../components';
import { Card, PageHeader, initialsOf } from '../dashboards/ui';

// Trainer Framework — reusable program templates. "Save as template" (on the
// Program tab) snapshots a client's curriculum here; from here a trainer can
// apply any template to a client — restoring a program they replaced, or reusing
// a proven method for a brand-new client. Applying clones fresh, independent rows.
const TemplatesPage: React.FC = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<ProgramTemplate[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [activeCompany, setActiveCompany] = useState<string | null>(null);
  const [applying, setApplying] = useState<string | null>(null);
  const [applied, setApplied] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // The AA Standard base program ("AAI Adoption Program") is provided to every
  // organization and is protected from deletion; every other template can be removed.
  const isStandard = (t: ProgramTemplate) => t.template_key === 'aa_standard' || t.tag === 'Standard';

  const load = useCallback(async () => {
    try { const { data } = await aaiApi.listTemplates(); setTemplates(data.templates || []); setError(null); }
    catch { setError('Could not load your templates.'); setTemplates([]); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const [ws, ctx] = await Promise.all([aaiApi.getWorkspaces(), aaiApi.getContext()]);
        setWorkspaces(ws.data.workspaces || []);
        setActiveCompany(ctx.data.company_id);
      } catch { /* the bar is optional — page works without it */ }
    })();
  }, []);

  const switchClient = (id: string) => { aaiApi.setAaiCompany(id); setActiveCompany(id); };

  const activeLabel = workspaces.find((w) => w.id === activeCompany)?.display_name
    || workspaces.find((w) => w.id === activeCompany)?.name || 'this client';

  const apply = async (t: ProgramTemplate) => {
    if (!activeCompany) { setNotice('Pick a client to apply this template to first.'); return; }
    if (!window.confirm(`Apply "${t.name}" to ${activeLabel}? This replaces their current program with this template's phases and modules.`)) return;
    setApplying(t.template_key);
    setNotice(null);
    try {
      await aaiApi.applyTemplate(activeCompany, { template_key: t.template_key });
      setApplied(t.template_key);
      setNotice(`Applied "${t.name}" to ${activeLabel}.`);
      setTimeout(() => setApplied(null), 2500);
    } catch {
      setNotice('Could not apply that template. Please try again.');
    } finally {
      setApplying(null);
    }
  };

  const remove = async (t: ProgramTemplate) => {
    if (!window.confirm(`Delete the template "${t.name}"? This removes the reusable blueprint. Programs already applied to clients are unaffected.`)) return;
    setDeleting(t.template_key);
    setNotice(null);
    try {
      await aaiApi.deleteTemplate(t.template_key);
      setTemplates((prev) => (prev || []).filter((x) => x.template_key !== t.template_key));
    } catch {
      setNotice('Could not delete that template. Please try again.');
    } finally {
      setDeleting(null);
    }
  };


  if (!templates && !error) return <Loading label="Loading your templates…" />;
  if (error) return <ErrorNote message={error} />;

  return (
    <div className="max-w-4xl mx-auto">
      <PageHeader
        title="Templates"
        subtitle="Reusable program blueprints. Save a client's program as a template from the Program tab, then apply it here — to restore a program you replaced, or to give a new client a proven method."
      />

      {/* Which client an applied template lands on. */}
      {workspaces.length > 0 && (
        <div className="flex items-center gap-2.5 flex-wrap mb-5">
          <span className="font-sans text-[10px] font-semibold uppercase tracking-[0.08em] text-gray-400">Apply to</span>
          {workspaces.map((w) => {
            const label = w.display_name || w.name || 'Workspace';
            const active = w.id === activeCompany;
            return (
              <button key={w.id} onClick={() => switchClient(w.id)}
                className={`whitespace-nowrap inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-[12.5px] font-semibold transition-colors ${active ? 'bg-brand-500 border-brand-500 text-white' : 'bg-white border-gray-200 text-gray-600 hover:border-brand-200'}`}>
                <span className={`grid place-items-center w-[18px] h-[18px] rounded-[5px] text-[9px] font-bold ${active ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'}`}>{initialsOf(label)}</span>
                {label}
              </button>
            );
          })}
        </div>
      )}

      {notice && (
        <div className="mb-4 rounded-lg bg-brand-50 border border-brand-100 px-3.5 py-2.5 text-[13px] text-brand-700">{notice}</div>
      )}

      {templates && templates.length === 0 ? (
        <Card className="text-center py-10">
          <span className="mx-auto mb-3 grid place-items-center h-14 w-14 rounded-full bg-gray-100 text-gray-400">
            <LayoutTemplate size={26} />
          </span>
          <p className="text-[15px] font-semibold text-gray-900 mb-1">No templates yet</p>
          <p className="text-[13px] text-gray-500 max-w-sm mx-auto mb-5">
            Build a program on the Program tab, then choose <b>Save as template</b> to keep it here — reusable for this client and any other.
          </p>
          <button onClick={() => navigate('/aai/curriculum')}
            className="inline-flex items-center gap-1.5 rounded-full bg-brand-500 hover:bg-brand-600 text-white text-[13px] font-semibold px-4 py-2 whitespace-nowrap">
            Go to the Program tab <ArrowRight size={15} />
          </button>
        </Card>
      ) : (
        <div className="space-y-2.5">
          {(templates || []).map((t) => (
            <Card key={t.template_key} className="flex items-start gap-3.5">
              <span className="w-10 h-10 rounded-lg bg-brand-50 text-brand-600 grid place-items-center shrink-0 mt-0.5">
                <Copy size={18} />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[14px] font-semibold text-gray-900 truncate">{t.name}</span>
                  {isStandard(t) ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 px-2 py-0.5 text-[10.5px] font-semibold" title="The organization's standard base program — protected from deletion">
                      <ShieldCheck size={11} /> Standard
                    </span>
                  ) : t.tag && (
                    <span className="rounded-full bg-gray-100 text-gray-500 px-2 py-0.5 text-[10.5px] font-semibold">{t.tag}</span>
                  )}
                </div>
                {t.description && <p className="text-[12.5px] text-gray-500 mt-0.5">{t.description}</p>}
              </div>
              <div className="shrink-0 flex items-center gap-1.5">
                <button onClick={() => apply(t)} disabled={applying !== null}
                  className="inline-flex items-center gap-1.5 rounded-full bg-brand-500 hover:bg-brand-600 disabled:opacity-50 text-white text-[12.5px] font-semibold px-3.5 py-1.5 whitespace-nowrap">
                  {applying === t.template_key ? <Loader2 size={13} className="animate-spin" />
                    : applied === t.template_key ? <><Check size={13} /> Applied</>
                    : <>Apply</>}
                </button>
                {!isStandard(t) && (
                  <button onClick={() => remove(t)} disabled={deleting !== null}
                    title="Delete this template"
                    className="inline-flex items-center justify-center rounded-full border border-gray-200 bg-white hover:border-red-300 hover:text-red-600 text-gray-400 disabled:opacity-50 w-8 h-8">
                    {deleting === t.template_key ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={14} />}
                  </button>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default TemplatesPage;
